<template>
  <div>
    <svg
      ref="svgcontainer"
      :viewbox="`0,0,${w},${h}`"
      :width="w"
      :height="h"
      class="linechart"
    ></svg>
  </div>
</template>

<script setup>
import { _Categories2Array } from "@/assets/baseTypes.js";
import * as d3 from "d3";

const props = defineProps({
  chartdata: {
    type: Array,
    default: () => [
      {
        2005: 15416158.46,
        2010: 14079397.38,
        2015: 5565801.93,
        2020: 7479107.65,
        2021: 8635185.67,
        short: "",
        name: "Andalucía",
        serie: [
          { yy: 2005, vv: 15416158.46 },
          { yy: 2010, vv: 14079397.38 },
          { yy: 2015, vv: 5565801.93 },
          { yy: 2020, vv: 7479107.65 },
          { yy: 2021, vv: 8635185.67 },
        ],
      },
      {
        2005: 2152000.0,
        2010: 5673059.13,
        2015: 3687312.51,
        2020: 3994628.3,
        2021: 3097342.86,
        short: "",
        name: "Aragón",
        serie: [
          { yy: 2005, vv: 2152000 },
          { yy: 2010, vv: 5673059.13 },
          { yy: 2015, vv: 3687312.51 },
          { yy: 2020, vv: 3994628.3 },
          { yy: 2021, vv: 3097342.86 },
        ],
      },
    ],
  },
  yyySeries: {
    type: Array,
    default: () => [2005, 2010, 2015, 2020, 2021],
  },
  availableWidth: {
    type: Number,
    default: 960,
  },
  fullDomain: {
    type: Array,
    default: () => [],
  },
  formatTools: {
    type: Function,
    default: (d) => d,
  },
});

const svgcontainer = ref(null);
let mySvg = null;

const w = computed(() => props.availableWidth);
const h = ref(500);

// - - Constants
const margin = 32;

onMounted(() => {
  mySvg = d3.select(svgcontainer.value);
  render();
});

watch(
  () => props.chartdata,
  () => {
    mySvg.selectAll("*").remove();
    render();
  }
);

watch(
  () => props.availableWidth,
  () => {
    mySvg.selectAll("*").remove();
    render();
  }
);

// copy yy_serie
// yyySeries =  [...yy_serie];

const lastYear = props.yyySeries[props.yyySeries.length - 1];
// check if last years = 0
const lastData = props.chartdata.map((e) => e.serie[e.serie.length - 1].vv);
const totLast = d3.sum(lastData);

if (totLast === 0) {
  props.yyySeries.pop();
}

// - - Scales
const x_scale = computed(() =>
  d3
    .scaleBand()
    .domain(props.yyySeries)
    .range([margin * 3, (w.value / 3) * 2])
);
const y_scale = computed(() =>
  d3
    .scaleLinear()
    .domain([_getMaxValue(props.yyySeries), _getMinValue(props.yyySeries)])
    .range([margin, h.value - margin])
    .nice()
);
// y_scale.value = d3.scaleLinear().domain([0, 10]).range([0,h.value]);

// - - Axis
const _a_x_scale = computed(() =>
  d3.axisBottom().scale(x_scale.value).tickSize(0).tickPadding(10)
);
const _a_y_scale = computed(() =>
  d3
    .axisLeft()
    .scale(y_scale.value)
    .tickSize(((w.value / 3) * 2 - margin * 3) * -1)
    .ticks(7)
    .tickFormat(props.formatTools)
);
//  this._eYaxis.scale(this._eYscl).ticks(4).tickSize(-this._eXscl.range()[1]);

// setup data series
// _packDataSeries(props.yyySeries);
const _categoricalColorScale = computed(() => {
  if (props.fullDomain && props.fullDomain.length > 0)
    return d3
      .scaleOrdinal(_Categories2Array)
      .domain(props.fullDomain.map((d) => parseInt(d.recipient_code)));
  else
    return d3
      .scaleOrdinal(_Categories2Array)
      .domain(props.chartdata.map((d) => parseInt(d._id)));
});
// - - Generators
// line curved
const Gen = computed(() =>
  d3
    .line()
    .x((p) => x_scale.value(p.yy) + x_scale.value.bandwidth() / 2)
    .y((p) => y_scale.value(p.vv))
    .curve(d3.curveMonotoneX)
);

// labels
let LablelsY = [];

// - - Catch interaction

// - - Render charts

function render() {
  _render_me();
}

// remove spaces and special characters
function _slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}
// - - - PRIVATE FUNCTIONS - - - //

function _render_me() {
  // this only will be used in this script
  // create svg elements

  const _mouseOver = (d) => {
    mySvg.selectAll(".line_parent").style("opacity", 0.1);
    mySvg.selectAll(".label").style("opacity", 0.1);
    mySvg.selectAll(`.thinline`).style("opacity", 0.01);
    mySvg.selectAll(`.label_dots`).style("opacity", 0.1);
    mySvg.selectAll(`.${_slugify(d.name)}`).style("opacity", 1);
    mySvg.selectAll(`.thinline.${_slugify(d.name)}`).style("opacity", 0.25);
  };
  const _mouseOut = (d) => {
    mySvg.selectAll(".label").style("opacity", 1);
    mySvg.selectAll(".line_parent").style("opacity", 1);
    mySvg.selectAll(`.thinline`).style("opacity", 0.25);
    mySvg.selectAll(`.label_dots`).style("opacity", 1);
  };
  // render axis
  mySvg
    .append("g")
    .attr("id", "xAxis")
    .attr("transform", "translate(0," + (h.value - margin + 8) + ")")
    .call(_a_x_scale.value);

  mySvg
    .append("g")
    .attr("id", "yAxis")
    .attr("transform", "translate(" + margin * 3 + ",0)")
    .call(_a_y_scale.value);

  // monotone lines
  const gline = mySvg
    .selectAll(".line_parent")
    .data(props.chartdata)
    .enter()
    .append("g")
    .attr("class", (d) => `line_parent ${_slugify(d.name)}`)
    .style("stroke", "#333")
    .attr("fill", (d, i) => _categoricalColorScale.value(d._id));
  gline
    .on("mouseover", (event, d) => {
      _mouseOver(d);
    })
    .on("mouseout", (event, d) => {
      _mouseOut(d);
    });

  // .attr("stroke", (d,i)=> this.categoricalScale(d.name))
  gline
    .append("path")
    .attr("class", "line_g")
    .attr("stroke-width", "3px")
    .attr("fill", "none")
    .attr("stroke", (d, i) => _categoricalColorScale.value(d._id))
    .attr("d", (d) => {
      return Gen.value(d.serie);
    });

  // dots
  mySvg
    .selectAll(".line_parent")
    .selectAll("circle")
    .data((d) => d.serie)
    .enter()
    .append("circle")
    .attr("stroke", "transparent")
    .attr("r", 3)
    .attr("cx", (d) => x_scale.value(d.yy) + x_scale.value.bandwidth() / 2)
    .attr("cy", (d) => y_scale.value(d.vv));

  // texts labels
  LablelsY = [];
  for (const entry of props.chartdata) {
    LablelsY.push(y_scale.value(entry.serie[entry.serie.length - 1].vv)); // last value
  }

  _relax(LablelsY);
  const ww = (w.value / 3) * 2;

  mySvg
    .append("g")
    .attr("id", "lines")
    .selectAll(".lines_labels")
    .data(props.chartdata)
    .enter()
    .append("line")
    .attr("class", (d) => `thinline ${_slugify(d.name)}`)
    .attr(
      "x1",
      (d) =>
        x_scale.value(d.serie[d.serie.length - 1].yy) +
        x_scale.value.bandwidth() / 2 +
        8
    )
    .attr("y1", (d) => y_scale.value(d.serie[d.serie.length - 1].vv))
    .attr("x2", ww + margin / 2)
    .attr("y2", (d, i) => LablelsY[i])
    .style("stroke", "black")
    .style("opacity", 0.25);

  // labels text
  mySvg
    .append("g")
    .attr("id", "labels")
    .selectAll(".label")
    .data(props.chartdata)
    .enter()
    .append("text")
    .attr("class", (d) => `label ${_slugify(d.name)}`)
    .attr("x", ww + margin)
    .attr("y", (d, i) => LablelsY[i])
    .attr("dy", "0.35em")
    .text(function (d) {
      return d.name;
    })
    .on("mouseover", (event, d) => {
      _mouseOver(d);
    })
    .on("mouseout", (event, d) => {
      _mouseOut(d);
    });

  mySvg
    .append("g")
    .attr("id", "label_dots")
    .selectAll(".label_dot")
    .data(props.chartdata)
    .enter()
    .append("circle")
    .attr("class", (d) => `label_dots ${_slugify(d.name)}`)
    .attr("cx", ww + margin / 2)
    .attr("cy", (d, i) => LablelsY[i])
    .attr("r", 3)
    .attr("fill", (d, i) => _categoricalColorScale.value(d._id));
}

function _update_me() {
  mySvg
    .selectAll("circle")
    .transition()
    .duration(500)
    .attr("r", () => Math.random() * 120);
}

function _getMaxValue(mySerie) {
  const maxValues = [];

  props.chartdata.forEach((linedata) => {
    maxValues.push(d3.max(linedata.serie, (d) => d.vv));
  });
  return d3.max(maxValues) * 1.05;
}

function _getMinValue(mySerie) {
  const minValues = [];
  props.chartdata.forEach((linedata) => {
    minValues.push(d3.min(linedata.serie, (d) => d.vv));
  });
  return d3.min(minValues);
}

function _relax(LablelsY) {
  const spacing = 18;
  const dy = 2;
  let repeat = false;
  let diff;
  let magnitude;
  let adjust;
  const hh = h.value;

  LablelsY.forEach(function (dA, i) {
    const yA = LablelsY[i];
    LablelsY.forEach(function (dB, j) {
      const yB = LablelsY[j];
      if (i === j) {
        return;
      }
      diff = yA - yB;
      if (Math.abs(diff) > spacing) {
        return;
      }
      repeat = true;
      magnitude = diff > 0 ? 1 : -1;
      adjust = magnitude * dy;
      LablelsY[i] = +yA + adjust;
      LablelsY[j] = +yB - adjust;
      LablelsY[j] = LablelsY[j] > hh - 16 ? hh - 16 : LablelsY[j];
      LablelsY[i] = LablelsY[i] > hh - 16 ? hh - 16 : LablelsY[i];
    });
  });
  if (repeat) {
    _relax(LablelsY);
  } else {
    return;
  }
}
</script>

<style lang="scss">
.linechart {
  .tick line {
    stroke: #ddd !important;
  }

  #yAxis .domain {
    display: none;
  }
  .tick text {
    font-size: 0.8rem;
    fill: #999;
  }
  #labels text.label {
    font-size: 0.75rem;
    font-weight: 400;
    fill: #000;
  }
}
</style>
